import style from './style.module.scss';
import React from 'react';
import { joinNames } from '../helpers';

type TextProps = React.PropsWithChildren & React.HTMLAttributes<HTMLHeadingElement>;
export type HeadlineLevel = 1|2|3|4|5|6;

type HeadlineProps = {
    /** defaults to 1 */
    level?: HeadlineLevel;
    /** If set to true, the element is rendered as a div-element decorated with aria-level semantics */
    asDivElement?: boolean;
};

export function Headline({ children, className, level = 1, asDivElement = false, ...attr }: TextProps & HeadlineProps): JSX.Element {
    className = getClassName(level, className);

    if (!asDivElement) {
        switch (level) {
            case 2:
                return <h2 {...attr} className={className}>{children}</h2>;
            case 3:
                return <h3 {...attr} className={className}>{children}</h3>;
            case 4:
                return <h4 {...attr} className={className}>{children}</h4>;
            case 5:
                return <h5 {...attr} className={className}>{children}</h5>;
            case 6:
                return <h6 {...attr} className={className}>{children}</h6>;
            case 1:
            default:
                return <h1 {...attr} className={className}>{children}</h1>;
        }
    } else {
        return (
            <div className={className} {...attr} role="heading" aria-level={level}>
                {children}
            </div>
        );
    }
}

export function HeadlineStyledSpan({ children, className, level, ...attr }: TextProps & {level: HeadlineLevel}): JSX.Element{
    className = getClassName(level, className);
    return <span {...attr} className={className}>{children}</span>;
} 

function getClassName(level: HeadlineLevel, className?: string) {
    switch (level) {
        case 2:
            return joinNames(style.headline2, className);
        case 3:
            return joinNames(style.headline3, className);
        case 4:
            return joinNames(style.headline4, className);
        case 5:
            return joinNames(style.headline5, className);
        case 6:
            return joinNames(style.headline6, className);
        case 1:
        default:
            return joinNames(style.headline1, className);
    }
}

export function Paragraph({ children, className, ...attr }: TextProps): JSX.Element {
    return (
        <div className={joinNames(style.paragraph, className)} {...attr}>
            {children}
        </div>
    );
}

export function Section({ children, className, ...attr }: TextProps): JSX.Element {
    return (
        <section className={joinNames(style.section, className)} {...attr}>
            {children}
        </section>
    );
}
